import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import BASE_URL from "../BASEURL";
import { ChevronLeft, ChevronRight, Phone, Briefcase, Award, Shield } from "lucide-react";

/* ───────────── HELPERS ───────────── */
const normalize = (str = "") =>
  str.toString().trim().toUpperCase().replace(/[\s\-–—]+/g, " ").trim();

const leadershipDesignations = [
  "PRESIDENT", "SECRETARY", "TREASURER", "VICE PRESIDENT", "JOINT SECRETARY", "LADIES REPRESENTATIVE"
].map(normalize);

const executivePriority = [
  "COMMITTEE MEMBER", "MEMBER", "CO-OPT MEMBER", "CO-OPTED MEMBER"
].map(normalize);

/* ───────────── MEMBER CARD ───────────── */
const MemberCard = ({ member, dark = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ y: -10 }}
    className={`group relative rounded-[2.5rem] overflow-hidden transition-all duration-500 shadow-xl 
      ${dark ? "bg-[#1A1A1A] border border-white/10" : "bg-white border border-gray-100"}`}
  >
    {/* Image Section */}
    <div className="relative h-[380px] sm:h-[420px] w-full overflow-hidden bg-gray-200">
      <img
        src={member.photo}
        alt={member.name}
        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
      />
      
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80" />
      
      <div className="absolute top-5 right-5 backdrop-blur-md bg-white/10 border border-white/20 px-4 py-1.5 rounded-full">
        <span className="text-white text-[10px] font-bold tracking-widest uppercase italic">
          Batch {member.batch}
        </span>
      </div>

      <div className="absolute bottom-6 left-6 right-6">
        <p className="text-[#D4A017] text-xs font-black tracking-[0.25em] uppercase mb-2">
          {member.designation}
        </p>
        <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
          {member.name}
        </h3>
      </div>
    </div>

    {/* Content Section */}
    <div className="p-6 sm:p-8 space-y-5">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-2xl ${dark ? "bg-white/5" : "bg-gray-50"}`}>
          <Briefcase size={20} className="text-[#D4A017]" />
        </div>
        <div>
          <p className={`text-[10px] uppercase tracking-wider font-bold ${dark ? "text-gray-500" : "text-gray-400"}`}>Current Role</p>
          <p className={`text-sm font-semibold ${dark ? "text-gray-200" : "text-gray-800"}`}>{member.role}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-2xl ${dark ? "bg-white/5" : "bg-gray-50"}`}>
          <Phone size={20} className="text-[#D4A017]" />
        </div>
        <div>
          <p className={`text-[10px] uppercase tracking-wider font-bold ${dark ? "text-gray-500" : "text-gray-400"}`}>Contact Info</p>
          <p className={`text-sm font-medium ${dark ? "text-gray-200" : "text-gray-800"}`}>{member.mob}</p>
        </div>
      </div>

      <div className={`pt-6 border-t ${dark ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
        <span className="text-[9px] text-[#D4A017] font-black uppercase tracking-[0.2em]">Verified Member</span>
        <Shield size={16} className="text-gray-400 opacity-30" />
      </div>
    </div>
  </motion.div>
);

/* ───────────── MAIN COMPONENT ───────────── */
const AssociationMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 640 : false);

  // Mobile detection with resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/persons`);
        const data = await response.json();
        const mappedData = data.map((item) => ({
          designation: normalize(item?.person_designation?.designation_name || item?.designation || ""),
          name: item?.person_name,
          photo: item?.person_photo,
          role: item?.person_role,
          mob: item?.person_mobile,
          batch: item?.person_batch,
        }));
        setMembers(mappedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  const keyOfficers = members
    .filter(m => leadershipDesignations.includes(m.designation))
    .sort((a, b) => leadershipDesignations.indexOf(a.designation) - leadershipDesignations.indexOf(b.designation));

  const executiveMembers = members.filter(m => executivePriority.includes(m.designation));
  const otherMembers = members.filter(
    m => !leadershipDesignations.includes(m.designation) && !executivePriority.includes(m.designation)
  );

  const maxIndex = Math.max(0, otherMembers.length - (isMobile ? 1 : 2));

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#0B0B0B]">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="text-[#D4A017] font-black tracking-[0.5em] text-xl"
        >
          GSAPPPA
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-[#FCFCFC] overflow-x-hidden font-sans">
      {/* HERO */}
      <section className="relative min-h-[85vh] sm:min-h-[90vh] flex items-center justify-center bg-[#0B0B0B] px-6 py-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-[#D4A017]/10 blur-[120px] rounded-full" />
          <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-[#D4A017]/5 blur-[120px] rounded-full" />
        </div>

        <div className="relative z-10 text-center max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-block px-6 py-2 rounded-full border border-[#D4A017]/30 mb-8 backdrop-blur-sm">
            <span className="text-[#D4A017] text-xs font-bold tracking-[0.3em] uppercase">Established 2025</span>
          </motion.div>
          
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-4xl sm:text-5xl font-black text-white mb-10 tracking-tight leading-none">
            THE <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4A017] via-[#f7e2a7] to-[#8c6a0f]">OFFICE</span> BEARERS
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-gray-400 text-lg sm:text-2xl max-w-3xl mx-auto leading-relaxed">
            Representing the core of legal excellence in Gujarat. Our team is dedicated to the advancement of justice and the professional growth of our members.
          </motion.p>
        </div>
      </section>

      {/* LEADERSHIP GRID */}
      <section className="relative z-30 max-w-7xl mx-auto px-6 -mt-20 sm:-mt-32 pb-20 sm:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
          {keyOfficers.map((member, idx) => (
            <MemberCard key={idx} member={member} />
          ))}
        </div>
      </section>

      {/* EXECUTIVE COMMITTEE */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <h2 className="text-5xl sm:text-6xl font-black text-gray-900 mb-4">Committee</h2>
              <div className="h-1.5 w-40 bg-[#D4A017]" />
            </div>
            <p className="text-gray-500 max-w-md">Our executive council focuses on strategic decisions and administrative excellence of the association.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {executiveMembers.map((member, idx) => (
              <MemberCard key={idx} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* EXTENDED LEADERSHIP SLIDER */}
      <section className="py-20 sm:py-32 bg-[#0B0B0B] text-white">
        <div className="max-w-7xl mx-auto px-6 mb-12 flex flex-col md:flex-row justify-between items-center gap-8">
          <h2 className="text-4xl sm:text-5xl font-black italic">Extended Leadership</h2>
          <div className="flex gap-4">
            <button
              onClick={() => setSliderIndex(prev => Math.max(0, prev - 1))}
              className="p-4 rounded-full border border-white/10 hover:bg-[#D4A017] hover:border-[#D4A017] transition-all"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => setSliderIndex(prev => Math.min(maxIndex, prev + 1))}
              className="p-4 rounded-full border border-white/10 hover:bg-[#D4A017] hover:border-[#D4A017] transition-all"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden px-6">
  <motion.div
    animate={{
      x: isMobile
        ? `-${sliderIndex * 100}%`   // mobile: full width slide
        : `-${sliderIndex * 33.33}%` // desktop: 3 cards view
    }}
    transition={{ type: "spring", stiffness: 70, damping: 20 }}
    className={`flex ${isMobile ? "gap-2" : "gap-6"} w-full`}
  >
    {otherMembers.map((member, idx) => (
      <div
        key={idx}
        className={`
          flex-shrink-0
          ${isMobile 
            ? "w-full"        // 📱 1 card full width
            : "w-1/3"}        // 💻 3 cards in row
        `}
      >
        <MemberCard member={member} dark={true} />
      </div>
    ))}
  </motion.div>
</div>

      </section>

      {/* CTA */}
      <section className="py-20 bg-white text-center px-6">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          className="max-w-4xl mx-auto"
        >
          <Award size={64} className="mx-auto text-[#D4A017] mb-10" />
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-8 tracking-tighter">JOIN OUR MISSION</h2>
          <p className="text-xl text-gray-500 mb-12 max-w-2xl mx-auto leading-relaxed">
            Support the legal backbone of Gujarat. Whether you are a member or a partner, let's uphold justice together.
          </p>
          <a
            href="/contact"
            className="inline-block bg-[#D4A017] hover:bg-black text-white px-10 py-5 rounded-full font-black text-lg transition-all transform hover:scale-110 hover:shadow-2xl"
          >
            GET IN TOUCH
          </a>
        </motion.div>
      </section>
    </div>
  );
};

export default AssociationMembers;